// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import type { SVGProps } from 'react';
import * as React from 'react';
import '@/styles/participant-placeholder.css';

interface ParticipantPlaceholderProps {
  props?: SVGProps<SVGSVGElement>;
  avatarUrl?: string;
  loaded?: boolean;
  isSpeaking?: boolean;
  audioLevel?: number;
  mutedOrSilent?: boolean;
}
const ParticipantPlaceholderAvatar = ({
  avatarUrl,
  loaded,
  isSpeaking,
  audioLevel = 0,
  mutedOrSilent = false,
  ...props
}: ParticipantPlaceholderProps) => {
  const startingRadius = 60;
  let maxRadius = startingRadius * 1.2;

  const radius = isSpeaking
    ? startingRadius + (maxRadius - startingRadius) * Math.min(audioLevel, 1)
    : startingRadius;

  const strokeColor = mutedOrSilent ? '#ef4444' : '#22c55e';
  const animationClass = mutedOrSilent ? 'pulsing-circle-muted-sync' : 'pulsing-circle-sync';

  return (
    <svg
      width={120}
      height={120}
      viewBox="0 0 240 240"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {avatarUrl ? (
        <>
          <image
            href={avatarUrl}
            x={60}
            y={60}
            width={120}
            height={120}
            preserveAspectRatio="xMidYMid meet"
            className={`avatar ${loaded ? ' loaded' : ''}`}
            clipPath="url(#circleClip)"
          />
          <defs>
            <clipPath id="circleClip">
              <circle cx={120} cy={120} r={60} />
            </clipPath>
          </defs>
        </>
      ) : (
        <>
          <path
            d="M160 180C204.182 180 240 144.183 240 100C240 55.8172 204.182 20 160 20C115.817 20 79.9997 55.8172 79.9997 100C79.9997 144.183 115.817 180 160 180Z"
            fill="currentColor"
            fillOpacity={0.15}
          />
          <path
            d="M97.6542 194.614C103.267 191.818 109.841 192.481 115.519 195.141C129.025 201.466 144.1 205 159.999 205C175.899 205 190.973 201.466 204.48 195.141C210.158 192.481 216.732 191.818 222.345 194.614C262.703 214.719 291.985 253.736 298.591 300.062C300.15 310.997 291.045 320 280 320H39.9997C28.954 320 19.8495 310.997 21.4087 300.062C28.014 253.736 57.2966 214.72 97.6542 194.614Z"
            fill="currentColor"
            fillOpacity={0.15}
          />
        </>
      )}
      {isSpeaking && (
        <circle
          cx={120}
          cy={120}
          r={radius}
          stroke={strokeColor}
          strokeWidth={4}
          fill="none"
          className={`${animationClass} smooth-stroke`}
          style={{
            opacity: Math.max(0.3, audioLevel),
          }}
        />
      )}

      <style>{`
        .pulsing-circle {
          animation: pulse 1s infinite ease-out;
        }
        .smooth-stroke {
          transition: stroke 250ms ease;
        }
        
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          30% {
            opacity: 0.6;
          }
          60% {
            opacity: 0.1;
          }
          100% {
            opacity: 0;
          }
        }
      `}</style>
    </svg>
  );
};

export default ParticipantPlaceholderAvatar;
